import { useCallback, useEffect, useState } from 'react'
import { FileRejection, useDropzone } from 'react-dropzone'
import Cropper, { Area } from 'react-easy-crop'
import { Box, Button, DialogActions, DialogContent, Grid, LinearProgress, Slider, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'

import { useHost } from '../../providers/app-provider'
import Api from '../../utils/api'
import useLoadDevicePhoto from '../../utils/hooks/useLoadDevicePhoto'
import useSharedStyles from '../../utils/shared.styles'
import DialogWithTitle from '../dialog-with-title'

import { getCroppedImg, handleDrop } from './helpers'
import useStyles from './index.styles'

interface IProps {
  deviceId: number
  open: boolean
  onClose: () => void
  onSave: (path: string) => void
  photoPath: string | null
  viewFullPhoto?: boolean
}

const DevicePhotoModal = (props: IProps) => {
  const { deviceId, open, onClose, photoPath, onSave, viewFullPhoto } = props
  const classes = useStyles
  const sharedClasses = useSharedStyles
  const { enqueueSnackbar } = useSnackbar()
  const host = useHost()
  const { photoPath: viewPhotoPath } = useLoadDevicePhoto(deviceId, !!viewFullPhoto, 'photo')

  const [viewMode, setViewMode] = useState(viewFullPhoto ? 'photo' : 'upload')
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)

  const [uploadedPhoto, setUploadedPhoto] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState<boolean>(false)

  const resetState = useCallback(() => {
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setCroppedAreaPixels(null)
    setUploadedPhoto(null)
    setViewMode(viewFullPhoto ? 'photo' : 'upload')
  }, [viewFullPhoto])

  useEffect(() => {
    if (!open) {
      setTimeout(() => resetState(), 200) // Timeout prevents flicker when closing modal
    }
  }, [open, resetState])

  // Handle file drop
  const onDrop = useCallback(
    <T extends File>(acceptedFiles: T[], fileRejections: FileRejection[]) => {
      handleDrop({ enqueueSnackbar, acceptedFiles, fileRejections, setUploadedPhoto })
    },
    [enqueueSnackbar]
  )

  const { getRootProps, getInputProps, isDragActive, inputRef } = useDropzone({
    onDrop,
    accept: globalThis.isJest ? {} : { 'image/*': ['.jpeg', '.jpg', '.png', '.gif'] },
    maxFiles: 1,
  })

  // Handle pasting images from clipboard
  useEffect(() => {
    const handlePasteFile = async (event: ClipboardEvent) => {
      if (event.clipboardData?.files && inputRef.current) {
        inputRef.current.files = event.clipboardData.files
        inputRef.current.dispatchEvent(new Event('change', { bubbles: true }))
      }
    }
    window.addEventListener('paste', handlePasteFile)
    return () => {
      window.removeEventListener('paste', handlePasteFile)
    }
  }, [inputRef])

  const handleSubmit = async () => {
    if (!uploadedPhoto || !croppedAreaPixels) return

    try {
      setIsUploading(true)
      const croppedBlob = await getCroppedImg(uploadedPhoto, croppedAreaPixels)
      const formData = new FormData()

      formData.append('photo', croppedBlob, 'photo.jpg')

      const response = await Api.put(`${host}/api/v2/devices/${deviceId}/photo`, formData)

      if (response.ok) {
        // Convert the cropped blob to an object URL for display in thumbnail
        // Note: The parent component is responsible for revoking this URL to prevent memory leaks
        const croppedImageUrl = URL.createObjectURL(croppedBlob)
        onSave(croppedImageUrl)
        resetState()
      } else {
        enqueueSnackbar('Error uploading photo. Please try again.', { variant: 'error' })
      }
    } catch {
      enqueueSnackbar('Error uploading photo. Please try again.', { variant: 'error' })
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <DialogWithTitle
      open={open}
      title="Device Photo"
      setOpen={onClose}
      maxWidth="md"
      slotProps={{
        paper: {
          sx: {
            minWidth: 650,
          },
        },
      }}
    >
      <DialogContent>
        {viewFullPhoto && viewMode === 'photo' && (
          <Grid container sx={[sharedClasses.centerContent, { flexDirection: 'column', gap: '10px' }]}>
            {viewPhotoPath ? (
              <img src={viewPhotoPath} alt="Photo of device" style={classes.preview} />
            ) : (
              <Grid container sx={[sharedClasses.centerContent, classes.preview]}>
                <LinearProgress sx={classes.fullPhotoLoader} />
              </Grid>
            )}
            <Button onClick={() => setViewMode('upload')}>Change Photo</Button>
          </Grid>
        )}
        {viewMode !== 'photo' && (
          <>
            {!uploadedPhoto ? (
              <form encType="multipart/form-data">
                <Grid container sx={[sharedClasses.centerContentVertical, { flexDirection: 'column' }]}>
                  {photoPath && !viewFullPhoto && <img src={photoPath} alt="Photo of device" style={classes.thumbnail} />}
                  <Box
                    sx={{
                      ...classes.dropzone,
                      ...(isDragActive ? classes.dropzoneActive : {}),
                    }}
                    {...getRootProps()}
                  >
                    <Grid
                      sx={{
                        marginTop: 4,
                      }}
                    >
                      <input id="device-photo-upload" aria-label="Device photo upload" {...getInputProps()} />
                      <Button variant="contained">Upload Image</Button>
                    </Grid>

                    <Grid
                      sx={{
                        marginTop: 3,
                        marginBottom: 4,
                      }}
                    >
                      <Typography variant="subtitle2">or Paste Image Here</Typography>
                    </Grid>
                  </Box>
                </Grid>
              </form>
            ) : (
              <>
                <>
                  <Box sx={classes.cropperContainer}>
                    <Cropper
                      image={uploadedPhoto}
                      crop={crop}
                      zoom={zoom}
                      aspect={1}
                      cropShape="round"
                      showGrid={false}
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onCropComplete={(_, croppedArea) => setCroppedAreaPixels(croppedArea)}
                    />
                  </Box>

                  <Box sx={{ mt: 2, px: 4 }}>
                    <Slider value={zoom} min={1} max={3} step={0.1} onChange={(_, z) => setZoom(z as number)} disabled={isUploading} />
                  </Box>
                </>

                <DialogActions>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      resetState()
                      setViewMode('upload')
                    }}
                  >
                    Cancel
                  </Button>

                  <Button variant="contained" onClick={handleSubmit} loading={isUploading} disabled={isUploading} data-testid="save-device-photo-button">
                    Save
                  </Button>
                </DialogActions>
              </>
            )}
          </>
        )}
      </DialogContent>
    </DialogWithTitle>
  )
}

export default DevicePhotoModal
